/**
 * MBA Lab — pure state model for the Coaching Question Deck. No DOM, storage,
 * or analytics dependencies - coaching-question-deck.js owns all of that and
 * consumes the functions below. State is just a saved/starred question list,
 * far smaller than every other tool's - there's no scoring or completion
 * gate, just a personal shortlist someone builds before a real 1:1.
 */

import { STEPS, QUESTIONS } from './coaching-questions.js';

export const SCHEMA_NAME = 'mbalab.coaching-question-deck';
export const SCHEMA_VERSION = 1;

export const LIMITS = Object.freeze({
  maxSaved: 12,
  maxSerializedBytes: 10 * 1024,
});

const QUESTIONS_BY_ID = new Map(QUESTIONS.map((q) => [q.id, q]));
export const TOTAL_QUESTIONS = QUESTIONS.length;

export function createInitialState() {
  return { schema: SCHEMA_NAME, version: SCHEMA_VERSION, savedAt: null, savedQuestionIds: [] };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const rawIds = Array.isArray(r.savedQuestionIds) ? r.savedQuestionIds : [];
  const seen = new Set();
  const savedQuestionIds = [];
  for (const id of rawIds) {
    if (savedQuestionIds.length >= LIMITS.maxSaved) break;
    if (typeof id !== 'string' || !QUESTIONS_BY_ID.has(id) || seen.has(id)) continue;
    seen.add(id);
    savedQuestionIds.push(id);
  }
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    savedQuestionIds,
  };
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's
  // identical note. Proves the mechanism works before it's ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function isSaved(state, id) {
  return state.savedQuestionIds.includes(id);
}

export function savedCount(state) {
  return state.savedQuestionIds.length;
}

export function isSavedFull(state) {
  return state.savedQuestionIds.length >= LIMITS.maxSaved;
}

/** No-op (not an error) once the cap is reached - the UI disables the "Save"
 * control at that point, so this only guards a direct/racing call. */
export function toggleSaved(state, id) {
  if (!QUESTIONS_BY_ID.has(id)) return state;
  if (state.savedQuestionIds.includes(id)) {
    return { ...state, savedQuestionIds: state.savedQuestionIds.filter((x) => x !== id) };
  }
  if (isSavedFull(state)) return state;
  return { ...state, savedQuestionIds: [...state.savedQuestionIds, id] };
}

export function clearSaved(state) {
  return { ...state, savedQuestionIds: [] };
}

/** Returned in fixed STEPS order (not save-order), so a printed/copied set
 * reads as a real Goal->Reality->Options->Will flow regardless of the order
 * questions were saved in. */
export function getSavedQuestions(state) {
  const idSet = new Set(state.savedQuestionIds);
  return QUESTIONS.filter((q) => idSet.has(q.id));
}

/** Plain text, not markdown - every string here is fixed, non-user-authored
 * question copy (never free text), so there's no injection surface to escape
 * against, unlike charter-model.js's buildMarkdown(). */
export function buildSetText(state) {
  const saved = getSavedQuestions(state);
  if (!saved.length) return '';

  const lines = ['Your coaching question set', ''];
  for (const step of STEPS) {
    const stepQuestions = saved.filter((q) => q.step === step.key);
    if (!stepQuestions.length) continue;
    lines.push(step.label.toUpperCase());
    for (const q of stepQuestions) lines.push(`- ${q.text}`);
    lines.push('');
  }
  lines.push('Built with the free MBA Lab coaching question deck - mbalab.net/tools/coaching-question-deck');
  return lines.join('\n');
}
